import { isArrayEmpty } from "../utils/array"
import { useOutsideClick } from "./hooks"
import { Msg } from "./Msg"
import { classes } from "./react"

interface Props<T, K> {

    items: T[]

    keySelector(item: T): K
    selectedKeys: K[]
    setSelectedKeys(items: K[]): void

    textSelector(item: T): string

    label: {
        default: string
    }

}

export function MultiSelect<T, K extends string | number>(props: Props<T, K>) {

    const { items, keySelector, textSelector, selectedKeys, setSelectedKeys, label } = props

    const [open, setOpen] = React.useState(false)

    const set = React.useMemo(() => new Set(selectedKeys), [selectedKeys])

    const keyboardSelectedIndex = useKeyboardHandler(open, {

        close: () => setOpen(false),

        items,

        dependencies: [selectedKeys],

        toggleItem: (item: T) => {
            const selected = set.has(keySelector(item))
            selectItem(item, !selected)
        }

    })

    const allSelected = (items && selectedKeys && (items.length > 0) && (items.length === selectedKeys.length))

    const isSelected = (key: K) => set.has(key)

    const rootRef = useOutsideClick(open, () => setOpen(false))

    const selectItem = (item: T, selected: boolean) => {
        const itemKey = keySelector(item)
        const selection = (items || []).map(keySelector).filter(k => k === itemKey ? selected : isSelected(k))
        setSelectedKeys(selection)
    }
    const selectAll = (select: boolean) => {
        const selection = select ? (items || []).map(keySelector) : []
        setSelectedKeys(selection)
    }

    const text = getButtonText(
        isArrayEmpty(items)
        || isArrayEmpty(selectedKeys)
        || items.every(i => set.has(keySelector(i)))
    )


    const selectAllLabel = allSelected
        ? <Msg>(Unselect all)</Msg>
        : <Msg>(Select all)</Msg>


    return <div ref={rootRef} className="oa-multiselect" style={{ position: "relative" }}>

        <button type="button" onClick={() => setOpen(!open)}>
            <span>
                {text}
            </span>
            <i className="fas fa-chevron-down"></i>
        </button>

        <ul style={{ margin: 0, display: (open ? "block" : "none") }} className="dropdown-menu">

            {
                items &&
                <>

                    <li>
                        <a onClick={() => selectAll(!allSelected)}>
                            <ListItemContent selected={allSelected} text={selectAllLabel} />
                        </a>
                    </li>

                    {

                        items
                            .map((item, index) => {
                                const key = keySelector(item)
                                return <ListItem key={key} item={item} selected={isSelected(key)} focused={index === keyboardSelectedIndex} />
                            })
                    }

                </>
            }
        </ul>

    </div >


    function ListItem({ item = undefined as T, selected = false, focused = false }) {

        const text = textSelector(item)

        return <li className={classes(selected && "selected", focused && "focused")}>
            <a onClick={() => selectItem(item, !selected)}>
                <ListItemContent {...{ selected, text }} />
            </a>
        </li>

    }

    function ListItemContent({ text = "" as React.ReactNode, selected = false }) {

        return <span>
            {
                selected
                    ? <i className="far fa-check-square"></i>
                    : <i className="far fa-square"></i>
            }
            {text}
        </span>

    }

    function getButtonText(allSelected: boolean) {

        return allSelected
            ? label.default
            : items.filter(i => set.has(keySelector(i))).map(textSelector).filter(t => !!t).join(", ")

    }


}


interface KeyboardHandler<T> {

    items: T[]
    dependencies: any[]

    toggleItem(item: T): void

    close(): void

}


function useKeyboardHandler<T>(open: boolean, handler: KeyboardHandler<T>) {

    const { items, close, toggleItem, dependencies } = handler

    const [selectedIndex, setSelectedIndex] = React.useState(-1)

    React.useEffect(() => {

        if (selectedIndex !== -1) {
            setSelectedIndex(-1)
        }

    }, [open, items])

    React.useEffect(() => {

        if (open && items) {

            const handleKeyDown = (e: KeyboardEvent) => {

                if (e.code == "Escape") {
                    close()
                    return
                }

                if ((e.code === "Enter") || (e.code === "Space")) {
                    e.preventDefault()
                    toggleItem(items[selectedIndex])
                    return
                }

                let i = 0
                const length = items.length

                if (e.code == "ArrowUp") {
                    i = - 1
                }
                else if (e.code == "ArrowDown") {
                    i = 1
                }

                if (i) {

                    i += selectedIndex
                    i = (i < 0) ? length - 1 : ((i >= length) ? 0 : i)

                    if (i !== selectedIndex) {
                        setSelectedIndex(i)
                    }

                }

            }

            document.addEventListener("keydown", handleKeyDown)

            return () => document.removeEventListener("keydown", handleKeyDown)

        }


    }, [...dependencies, open, items, selectedIndex])

    return selectedIndex

}
